import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { AuthService } from '@/lib/firebase';
import { LegalFooter } from '@/components/LegalFooter';

const authService = new AuthService();

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const { signIn } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      await signIn(email, password);
    } catch (error: any) {
      setError(error.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      setResetMessage('');
      setError('Please enter your email address');
      return;
    }

    try {
      setResetSending(true);
      setError('');
      setResetMessage('');
      await authService.resetPassword(resetEmail);
      setResetMessage('Password reset email sent. Check your inbox.');
    } catch (error: any) {
      setError(error.message || 'Failed to send password reset email');
    } finally {
      setResetSending(false);
    }
  };

  return (
    <div className="screen-container flex items-center justify-center bg-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full animate-scale-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-secondary-900 tracking-tight mb-2">
            Service Strike
          </h1>
          <p className="text-secondary-600">
            {showForgotPassword ? 'Reset your password' : 'Sign in to your account'}
          </p>
          <p className="mt-2 text-sm text-secondary-500">
            Field service management for scheduling, dispatch, invoicing and payments.
          </p>
        </div>

        <div className="card">
          {showForgotPassword ? (
            <form className="space-y-6" onSubmit={handleForgotPassword}>
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-secondary-700 mb-2">
                  Email address
                </label>
                <input
                  id="reset-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-field"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-error-50 border border-error-200">
                  <p className="text-sm text-error-700">{error}</p>
                </div>
              )}

              {resetMessage && (
                <div className="p-3 rounded-xl bg-green-50 border border-green-200">
                  <p className="text-sm text-green-700">{resetMessage}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={resetSending}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {resetSending ? (
                  <>
                    <div className="loading-spinner-sm"></div>
                    Sending...
                  </>
                ) : (
                  'Send Reset Email'
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(false); setError(''); setResetMessage(''); }}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleLogin}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="input-field"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="input-field"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setError(''); setResetEmail(email); }}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-error-50 border border-error-200">
                  <p className="text-sm text-error-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="loading-spinner-sm"></div>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          )}
        </div>

        <LegalFooter />
      </div>
    </div>
  );
}
